/**
 *
 *  Contact Us page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 21/04/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import { Link } from "react-router-dom";
import { useAppState } from "../../state";

// Components
import Header from "../Homepage/Header/Header";
import Footer from "../Footer/Footer";
import Button from "../Button/Button";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

import { clientWidth } from "../../utils";

// Images
import hero from "../../images/partnerships-hero.jpg";
import hero2x from "../../images/partnerships-hero@2x.jpg";
import heroMobile from "../../images/partnerships-hero-mobile.jpg";
import heroMobile2x from "../../images/partnerships-hero-mobile@2x.jpg";

import TruBeLogoVertical from "./TruBeLogoVerticalWhite";

import dogPoundLogo from "./dogpound-black-logo.png";
import dogPoundLogo2x from "./dogpound-black-logo@2x.png";
import dogPoundLogo3x from "./dogpound-black-logo@3x.png";

import clubConnectLogo from "./club-connect-logo.png";
import clubConnectLogo2x from "./club-connect-logo@2x.png";
import clubConnectLogo3x from "./club-connect-logo@3x.png";

import nasmLogo from "./national-academy-of-sports-medicine-nasm-logo.png";
import nasmLogo2x from "./national-academy-of-sports-medicine-nasm-logo@2x.png";
import nasmLogo3x from "./national-academy-of-sports-medicine-nasm-logo@3x.png";

import barrysLogo from "./barrys-logo.png";
import barrysLogo2x from "./barrys-logo@2x.png";
import barrysLogo3x from "./barrys-logo@3x.png";

import tenxHealthLogo from "./10x-health-logo.png";
import tenxHealthLogo2x from "./10x-health-logo@2x.png";
import tenxHealthLogo3x from "./10x-health-logo@3x.png";

import kromaLogo from "./kroma-logo.png";
import kromaLogo2x from "./kroma-logo@2x.png";
import kromaLogo3x from "./kroma-logo@3x.png";

import centredLogo from "./centred-logo.png";
import centredLogo2x from "./centred-logo@2x.png";
import centredLogo3x from "./centred-logo@3x.png";

export default function PartnershipsPage() {
  const { theme } = useTheme();

  const { user } = useAppState();

  const heroRef = React.useRef(null);

  return (
    <>
      <ScrollToTopOnMount />
      <Header
        behaviour="none"
        isLoggedIn={!!(user && user.id)}
        heroRef={heroRef}
      />

      <div
        ref={heroRef}
        className="tbk-relative tbk-h-116 tbk-bg-grey-light tbk-pt-7 md:tbk-h-180"
      >
        <div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-top-7 tbk-left-0 tbk-flex tbk-w-full tbk-items-end tbk-overflow-hidden tbk-bg-grey-light">
          <picture className="tbk-h-full tbk-grow">
            <source
              media="(min-width: 768px)"
              srcSet={`${hero} 1x, ${hero2x} 2x, ${hero2x} 3x`}
            />
            <source
              srcSet={`${heroMobile} 1x, ${heroMobile2x} 2x, ${heroMobile2x} 3x`}
            />

            <img
              className="tbk-w-full tbk-object-cover md:tbk-h-full"
              src={hero}
              alt="Hero"
            />
          </picture>
        </div>
        <div className="tbk-container tbk-relative tbk-z-1 tbk-mx-auto tbk-h-full tbk-px-2 tbk-pb-4 lg:tbk-px-8 lg:tbk-pb-7">
          <div className="tbk-flex tbk-h-full tbk-items-end">
            <div>
              <h1 className="tbk-text-epic-medium md:tbk-text-extra-bold tbk-mb-3 !tbk-leading-none tbk-text-basic-white md:!tbk-leading-small">
                We're All About
                <br />
                <span className="tbk-text-coral">Relationships.</span>
              </h1>
              <Link to="/contact">
                <Button color="coral" className="tbk-inline-block tbk-w-68">
                  Let's Collab
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-7 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <p className="tbk-text-title-bold lg:tbk-text-epic-medium tbk-mb-7 tbk-text-center !tbk-font-semibold !tbk-tracking-tighter tbk-text-grey-main">
            We use great storytelling techniques to build our brand
            relationships, and like to invite partners and industry friends to
            add value through shared brand equity and best-in-class service.
          </p>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-0 lg:tbk-pt-8 lg:tbk-pb-0">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <h2 className="tbk-text-h2-black lg:tbk-text-extra-bold tbk-mb-7 tbk-text-center tbk-text-grey-main">
            We <span className="tbk-text-coral">Love</span> Our
            <br className="lg:tbk-hidden" /> Partners.
          </h2>
          <div className="tbk-mb-6 md:tbk-pb-0.5">
            <div className="tbk-flex tbk-flex-wrap tbk-items-center tbk-justify-center lg:tbk-mb-0 lg:tbk-flex-nowrap">
              <div className="tbk-flex tbk-w-full tbk-justify-center lg:tbk-w-auto ">
                <img
                  className="tbk-mb-3 lg:tbk-mr-7 lg:tbk-mb-8"
                  src={clubConnectLogo}
                  srcSet={`${clubConnectLogo} 1x, ${clubConnectLogo2x} 2x, ${clubConnectLogo3x} 3x`}
                  alt="ClubConnect"
                  style={{
                    width:
                      clientWidth() < theme.responsive.breakpoints.sm
                        ? "198px"
                        : "267px",
                    height:
                      clientWidth() < theme.responsive.breakpoints.sm
                        ? "64px"
                        : "86px",
                  }}
                />
              </div>
              <img
                className="tbk-mr-3 tbk-mb-4 lg:tbk--order-1 lg:tbk-mr-7 lg:tbk-mb-8"
                src={dogPoundLogo}
                srcSet={`${dogPoundLogo} 1x, ${dogPoundLogo2x} 2x, ${dogPoundLogo3x} 3x`}
                alt="DogPound"
                style={{
                  width:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "113px"
                      : "153px",
                  height:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "64px"
                      : "86px",
                }}
              />
              <img
                className="tbk-mb-4 tbk-hidden lg:tbk-mb-8 lg:tbk-block"
                src={nasmLogo}
                srcSet={`${nasmLogo} 1x, ${nasmLogo2x} 2x, ${nasmLogo3x} 3x`}
                alt="NASM"
                style={{
                  width: "166px",
                  height: "86px",
                }}
              />
              <img
                className="tbk-mb-4 lg:tbk-hidden"
                src={barrysLogo}
                srcSet={`${barrysLogo} 1x, ${barrysLogo2x} 2x, ${barrysLogo3x} 3x`}
                alt="Barry's"
                style={{
                  width: "116px",
                  height: "64px",
                }}
              />
            </div>
            <div className="tbk-flex tbk-flex-wrap tbk-items-center tbk-justify-center lg:tbk-flex-nowrap">
              <img
                className="tbk--order-1 tbk-mb-3 tbk-mr-3 lg:tbk-mb-8 lg:tbk-hidden"
                src={nasmLogo}
                srcSet={`${nasmLogo} 1x, ${nasmLogo2x} 2x, ${nasmLogo3x} 3x`}
                alt="NASM"
                style={{
                  width: "123px",
                  height: "64px",
                }}
              />
              <img
                className="tbk-mr-7 tbk-hidden lg:tbk-block"
                src={barrysLogo}
                srcSet={`${barrysLogo} 1x, ${barrysLogo2x} 2x, ${barrysLogo3x} 3x`}
                alt="Barry's"
                style={{
                  width: "156px",
                  height: "86px",
                }}
              />
              <img
                className="tbk-mr-3 tbk-mb-3 lg:tbk-mb-0 lg:tbk-mr-7"
                src={tenxHealthLogo}
                srcSet={`${tenxHealthLogo} 1x, ${tenxHealthLogo2x} 2x, ${tenxHealthLogo3x} 3x`}
                alt="10X Health"
                style={{
                  width:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "167px"
                      : "225px",
                  height:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "64px"
                      : "86px",
                }}
              />
              <img
                className="tbk-mb-3 lg:tbk-mr-7 lg:tbk-mb-0"
                src={kromaLogo}
                srcSet={`${kromaLogo} 1x, ${kromaLogo2x} 2x, ${kromaLogo3x} 3x`}
                alt="kroma."
                style={{
                  width:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "116px"
                      : "157px",
                  height:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "64px"
                      : "86px",
                }}
              />
              <img
                className="tbk--order-1 tbk-mb-3 md:tbk-mr-3 lg:tbk-order-none lg:tbk-mb-0"
                src={centredLogo}
                srcSet={`${centredLogo} 1x, ${centredLogo2x} 2x, ${centredLogo3x} 3x`}
                alt="Centred"
                style={{
                  width:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "103px"
                      : "128px",
                  height:
                    clientWidth() < theme.responsive.breakpoints.sm
                      ? "88px"
                      : "108",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-coral tbk-pt-7 tbk-pb-7 lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <p className="tbk-text-title-bold lg:tbk-text-epic-medium tbk-mb-7 tbk-text-center !tbk-font-semibold !tbk-tracking-tighter tbk-text-basic-white">
            We use great storytelling techniques to build our brand
            relationships, and like to invite partners and industry friends to
            add value through shared brand equity and best-in-class service
          </p>
          <div className="tbk-mb-1 tbk-text-center">
            <TruBeLogoVertical className="tbk-inline-block tbk-text-basic-white" />
          </div>
        </div>
      </div>

      <div className="tbk-h-108 tbk-bg-partnerships-lifting-mobile tbk-bg-cover tbk-pt-7 tbk-pb-7 lg:tbk-h-180 lg:tbk-bg-partnerships-lifting lg:tbk-pt-8 lg:tbk-pb-8" />
      <Footer />
    </>
  );
}
